<div class="tw:w-full tw:p-3">
  <div class="tw:flex tw:flex-wrap tw:gap-4 tw:mb-3">
    @for (card of cards(); track $index) {
      <div
        class="tw:flex-1 card tw:text-center"
        test-id="job-board-card">
        <div class="card-body">
          <div
            class="tw:text-5xl tw:font-semibold"
            [ngClass]="card.statusClass">
            {{ card.count }}
          </div>
          <div
            class="tw:text-xl tw:font-medium"
            [pTooltip]="card.tooltipData"
            tooltipPosition="bottom">
            {{ card.title }}
          </div>
        </div>
      </div>
    }
  </div>

  @if (isJobDataLoading()) {
    <cor-skeleton [type]="SKELETON_TYPES.GRID" />
  }

  <dx-data-grid
    #jobBoardGrid
    corGridSearch
    corGridSearchLocation="after"
    [wordWrapEnabled]="true"
    [hoverStateEnabled]="true"
    [showColumnLines]="false"
    [columnHidingEnabled]="true"
    [dataSource]="jobDataSource"
    [scrolling]="{ useNative: true }"
    [columnAutoWidth]="true"
    [allowColumnReordering]="true"
    [allowColumnResizing]="true"
    [visible]="!isJobDataLoading()"
    [elementAttr]="{ class: 'data-grid-row-clickable' }"
    (onRowClick)="onRowClick($event)"
    noDataText="No background jobs to show at the moment"
    (onContextMenuPreparing)="onContextMenuPreparing($event)">
    <dxo-data-grid-paging [pageSize]="pageSize()" />
    <dxo-data-grid-load-panel [enabled]="true" />
    <dxo-data-grid-pager
      [showPageSizeSelector]="true"
      [allowedPageSizes]="[10, 20, 50]"
      [showNavigationButtons]="true"
      [showInfo]="true" />
    <dxo-data-grid-header-filter
      [visible]="true"
      height="50vh"
      width="100%"
      [search]="{ enabled: true }" />
    <dxo-data-grid-toolbar>
      <dxi-data-grid-item>
        <p-button
          test-id="job-board-choose-columns"
          [buttonProps]="{ autofocus: false }"
          pTooltip="Choose Columns"
          tooltipPosition="left"
          severity="secondary"
          variant="outlined"
          icon="pi pi-list"
          (click)="columnChooserPopover.toggle($event)" />
        <p-popover
          #columnChooserPopover
          (onShow)="DataGridHelpers.setVisibleColumnFields(jobBoardGrid.instance, columnListbox)">
          @let jobBoardGridColumns = jobBoardGrid.instance?.option('columns') || [];
          <p-listbox
            test-id="job-board-on-visible-columns-change"
            #columnListbox
            [options]="jobBoardGridColumns"
            (onChange)="DataGridHelpers.onVisibleColumnsChange(jobBoardGrid.instance, $event)"
            [multiple]="true"
            [checkbox]="true"
            optionLabel="caption"
            optionValue="dataField"
            [filter]="true"
            filterBy="caption,dataField"
            [highlightOnSelect]="false"
            [showToggleAll]="true"
            scrollHeight="min(50rem, 50vh)"
            [style]="{
              width: 'min(30rem, 80vw)',
              '--p-listbox-border-color': 'transparent',
            }" />
        </p-popover>
      </dxi-data-grid-item>
    </dxo-data-grid-toolbar>

    <dxi-data-grid-column
      caption="Job Name"
      dataField="jobName"
      cellTemplate="jobNameTemplate" />

    <dxi-data-grid-column
      caption="Object Name"
      dataField="objectName" />

    <dxi-data-grid-column
      caption="Object Type"
      dataField="objectType" />

    <dxi-data-grid-column
      caption="Status"
      dataField="status"
      cellTemplate="statusTemplate" />

    <dxi-data-grid-column
      caption="Run Time"
      dataField="runTime"
      alignment="left"
      cellTemplate="durationTemplate" />

    <dxi-data-grid-column
      caption="Compile Time"
      [visible]="false"
      dataField="compileTime"
      alignment="left"
      cellTemplate="durationTemplate" />

    <dxi-data-grid-column
      caption="Queue Time"
      [visible]="false"
      dataField="queueTime"
      alignment="left"
      cellTemplate="durationTemplate" />

    <!-- FIXME: Ideally, we should use dataType="date" which formats the header-filter correctly.
                But unable to get that working with the right moment commands. So, using dataType=string for now. -->
    <dxi-data-grid-column
      caption="Time Created"
      [visible]="false"
      dataField="createdTime"
      dataType="string"
      cellTemplate="dateTemplate" />
    <dxi-data-grid-column
      caption="Time Submitted"
      [visible]="false"
      dataField="submittedTime"
      dataType="string"
      cellTemplate="dateTemplate" />
    <dxi-data-grid-column
      caption="Time Started"
      [visible]="false"
      dataField="startTime"
      dataType="string"
      cellTemplate="dateTemplate" />

    <dxi-data-grid-column
      caption="Queue Position"
      dataField="queuePosition"
      alignment="left" />

    <dxi-data-grid-column
      caption="Job Owner"
      dataField="createdBy"
      cellTemplate="avatarTemplate" />

    <dxi-data-grid-column
      caption="Description"
      [visible]="false"
      dataField="description" />

    <div
      class="tw:flex tw:flex-row"
      *dxTemplate="let data of 'jobNameTemplate'">
      <a
        test-id="job-board-data-data-has-access-you-do-not-have-acc"
        class="tw:truncate"
        [routerLink]="data.data.hasAccess ? data.data.url : null"
        pTooltip="data.data.hasAccess ? '' : 'You do not have access to this job'">
        {{ data.data.jobName }}
      </a>
    </div>

    <div
      class="tw:flex tw:flex-row tw:items-center"
      *dxTemplate="let data of 'statusTemplate'">
      <cor-status-icon [status]="data.value" />

      <div style="cursor: default">
        <cor-job-runtime-info
          [jobSteps]="data.data.jobSteps"
          (showEvent)="onShowHideRuntimeInfo($event)"
          (hideEvent)="onShowHideRuntimeInfo($event)"
          (click)="$event.stopPropagation()" />
      </div>
    </div>

    <div
      class="tw:flex tw:flex-row"
      *dxTemplate="let data of 'durationTemplate'">
      {{ data.value ? (data.value | timeTaken) : 'N/A' }}
    </div>

    <div *dxTemplate="let data of 'dateTemplate'">
      {{ data.value | localDate }}
    </div>

    <div *dxTemplate="let row of 'avatarTemplate'">
      <div class="tw:flex tw:items-center tw:gap-2">
        <p-avatar
          class="tw:shrink-0 tw:w-[1.5rem] tw:h-[1.5rem] tw:text-xs"
          [label]="row.value | nameBadge"
          [ngStyle]="{
            backgroundColor: (row.value | uniqueRgbColor),
            color: 'white',
          }"
          shape="circle" />
        <span>{{ row.value }}</span>
      </div>
    </div>
  </dx-data-grid>
</div>
